<template>
  <div class="tw-mt-10 lg:tw-mr-96">
    <div class="tw-mb-10 tw-pr-8">
      <div class="tw-pt-4 tw-outline tw-outline-0 tw-relative tw-border-b-2 focus-within:tw-border-orange-medium">
        <input 
          v-model="state.credit_card.name"
          type="text" 
          name="name" 
          placeholder=" "
          class="tw-block tw-text-2xl tw-py-4 tw-w-full tw-border-0 tw-appearance-none focus:tw-outline-0 tw-bg-transparent tw-border-transparent focus:tw-border-transparent focus:tw-ring-0 tw-px-0" />
        <label for="name" class="tw-pointer-events-none tw-absolute tw-top-0 tw-py-4 tw--z-1 tw-duration-300 tw-origin-top-left">Name on Card</label>
      </div>
    </div>

    <div class="tw-mb-10 tw-pr-8">
      <div class="tw-pt-4 tw-outline tw-outline-0 tw-relative tw-border-b-2 focus-within:tw-border-orange-medium">
        <input 
          v-model="state.credit_card.number"
          type="text" 
          name="number" 
          placeholder=" "
          class="tw-block tw-text-2xl tw-py-4 tw-w-full tw-border-0 tw-appearance-none focus:tw-outline-0 tw-bg-transparent tw-border-transparent focus:tw-border-transparent focus:tw-ring-0 tw-px-0" />
        <label for="number" class="tw-pointer-events-none tw-absolute tw-top-0 tw-py-4 tw--z-1 tw-duration-300 tw-origin-top-left">Number</label>
      </div>
    </div>

    <div>
      <label for="name" class="tw-pointer-events-none">Expiration</label>
    </div>

    <div class="tw-flex tw-flex-wrap">
      <div class="tw-mb-10 tw-w-1/2 tw-pr-8">
        <div class="tw-pt-4 tw-outline tw-outline-0 tw-relative tw-border-b-2 focus-within:tw-border-orange-medium">
          <select 
            :value="state.credit_card.exp_month"
            type="text" 
            name="exp_month"
            @change="setExpMonth"
            class="tw-block tw-text-2xl tw-py-4 tw-w-full tw-border-0 tw-appearance-none focus:tw-outline-0 tw-bg-transparent tw-border-transparent focus:tw-border-transparent focus:tw-ring-0 tw-px-0">
              <option :value="0" disabled>Month</option>
              <option 
                :value="m" v-for="m in [1,2,3,4,5,6,7,8,9,10,11,12]"
                :key="'month_'+m.toString()">{{m}}</option>

          </select>
        </div>
      </div>

      <div class="tw-mb-10 tw-w-1/2 tw-pl-8">
        <div class="tw-pt-4 tw-outline tw-outline-0 tw-relative tw-border-b-2 focus-within:tw-border-orange-medium">
          <select 
            :value="state.credit_card.exp_year"
            type="text" 
            name="exp_year" 
            @change="setExpYear"
            class="tw-block tw-text-2xl tw-py-4 tw-w-full tw-border-0 tw-appearance-none focus:tw-outline-0 tw-bg-transparent tw-border-transparent focus:tw-border-transparent focus:tw-ring-0 tw-px-0">
              <option :value="0" disabled>Year</option>
              <template v-if="state.nextTenYears">
                <option 
                  :value="y.toString()" v-for="y in state.nextTenYears"
                  :key="'y'+y.toString()">{{y}}</option>
              </template>
          </select>
        </div>
      </div>
    </div>

    <div class="tw-flex tw-flex-wrap tw-mb-8">
      <div class="tw-mb-10 tw-w-1/2 tw-pr-8">
        <div class="tw-pt-4 tw-outline tw-outline-0 tw-relative tw-border-b-2 focus-within:tw-border-orange-medium">
          <input 
            v-model="state.credit_card.cvv"
            type="text" 
            name="cvv" 
            placeholder=" "
            class="tw-block tw-text-2xl tw-py-4 tw-w-full tw-border-0 tw-appearance-none focus:tw-outline-0 tw-bg-transparent tw-border-transparent focus:tw-border-transparent focus:tw-ring-0 tw-px-0" />
          <label for="cvv" class="tw-pointer-events-none tw-absolute tw-top-0 tw-py-4 tw--z-1 tw-duration-300 tw-origin-top-left">CVV</label>
        </div>
      </div>

      <div class="tw-mb-10 tw-w-1/2 tw-pl-8">
        <div class="tw-pt-4 tw-outline tw-outline-0 tw-relative tw-border-b-2 focus-within:tw-border-orange-medium">
          <input 
            v-model="state.credit_card.zip"
            type="text" 
            name="zip" 
            placeholder=" "
            class="tw-block tw-text-2xl tw-py-4 tw-w-full tw-border-0 tw-appearance-none focus:tw-outline-0 tw-bg-transparent tw-border-transparent focus:tw-border-transparent focus:tw-ring-0 tw-px-0" />
          <label for="zip" class="tw-pointer-events-none tw-absolute tw-top-0 tw-py-4 tw--z-1 tw-duration-300 tw-origin-top-left">Billing ZIP</label>
        </div>
      </div>

    </div>


    <div class="tw-mb-12" v-if="canSaveCard">
      <TwCheckbox
        id="cc_type_new"
        key="cc_type_new"
        label="Save For Future Use"
        value="state.credit_card.saveonfile"
        class="tw-mb-4"
        :checked="state.credit_card.saveonfile"
        @on-change="state.credit_card.saveonfile = !state.credit_card.saveonfile"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    reactive, computed, onMounted, ref, watch
  } from 'vue';
  import type {PropType} from 'vue';
import { DateTime } from "luxon";

  import type BillingAccount from '../../classes/BillingAccount'
  import type BasketBillingMethod from '../../classes/BasketBillingMethod'
  import CreditCard from '../../classes/CreditCard'

  import TwCheckbox from '../../components/TwCheckbox.vue'
  import TwButton from '../../components/TwButton.vue'
  import BillingAccountCard from './_BillingAccountCard.vue'

  const props = defineProps({
    billingAccounts: {
      type: (Object as PropType<BillingAccount[]>),
      default: [],
    },
    newCC: {
      type: (Object as PropType<CreditCard> | null),
      required: false,
      default: null,
    },
    canSaveCard: {
      type: Boolean,
      default: false
    }
  });

  const state = reactive({
   credit_card: new CreditCard('', '','',0,0,'',false),
   nextTenYears: [] as number[],
  })

  const emit = defineEmits(['setCreditCard'])

  const setExpMonth = ((event: Event) => {
    const target = event.target as HTMLSelectElement;
    state.credit_card.exp_month = parseFloat(target.value);
  })

  const setExpYear = ((event: Event) => {
    const target = event.target as HTMLSelectElement;
    state.credit_card.exp_year = parseFloat(target.value);
  })

  onMounted(() => {
    const current_year = parseFloat(DateTime.now().toFormat('yy'));
    state.nextTenYears = [];
    for (var i = current_year; i <=( current_year + 10); i++) {
      state.nextTenYears.push(i);
    }

  })

  watch(() => state.credit_card, (newCC, oldCC) =>  {
      applyCard();
    },
    { deep: true }
  )

  watch(() => props.newCC, (newCC, oldCC) => { 
    
  })

  const applyCard = (() => {
    emit('setCreditCard', state.credit_card)
  })

</script>